// InAppDocViewer/pdfjs.js
//
// Lazy, code-split loader for PDF.js (pdfjs-dist). The library is ~heavy, and the
// audit flagged the already-large (~4.7 MB) main bundle, so PDF.js MUST NOT be in
// the main chunk. We load it with a dynamic import() the first time a PDF is
// actually viewed or previewed; webpack splits it into its own async chunk.
//
// We import the `legacy` build on purpose: react-scripts 5 / CRA's webpack config
// cannot handle pdfjs-dist's modern top-level-await ESM entry without ejecting.
// The legacy build loads cleanly under CRA 5.
//
// The worker is configured via workerSrc pointed at the matching legacy worker
// asset. We resolve its URL through the bundler (`new URL(..., import.meta.url)`)
// so the worker ships as a hashed asset rather than being inlined.
//
// Pin: pdfjs-dist@3.11.174 (last 3.x; CRA-5 safe). See package.json + PR notes.

let _pdfjsPromise = null;

/**
 * Resolve to the configured pdfjs module (singleton). Safe to call repeatedly;
 * the dynamic import + worker wiring happens exactly once.
 */
export function loadPdfjs() {
  if (_pdfjsPromise) return _pdfjsPromise;

  _pdfjsPromise = import(
    /* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf"
  )
    .then((pdfjs) => {
      // Worker served same-origin from public/pdf.worker.min.js, vendored at
      // install/build time by scripts/copy-pdf-worker.js from the SAME pinned
      // pdfjs-dist version (so API and worker versions always match). We use
      // ONLY this same-origin worker and do NOT fall back to a CDN: a remote
      // script with no Subresource Integrity is a supply-chain risk, and if the
      // worker fails to load the callers already degrade to the static
      // thumbnail + open-externally path.
      //
      // ISP-4017 #3: the previous `new URL("pdfjs-dist/...", import.meta.url)`
      // used a BARE specifier, which webpack 5 / CRA 5 does not rewrite, so the
      // worker never loaded. PUBLIC_URL keeps it correct under a sub-path deploy.
      const publicUrl =
        (typeof process !== "undefined" && process.env && process.env.PUBLIC_URL) ||
        "";
      pdfjs.GlobalWorkerOptions.workerSrc = `${publicUrl}/pdf.worker.min.js`;
      return pdfjs;
    })
    .catch((err) => {
      // Reset so a transient failure can be retried on the next interaction.
      _pdfjsPromise = null;
      throw err;
    });

  return _pdfjsPromise;
}

/**
 * Load a PDF document from a URL and return the pdfjs PDFDocumentProxy.
 * Throws on failure (callers fall back to the thumbnail + open-externally path).
 *
 * Security: `isEvalSupported: false` neutralizes CVE-2024-4367 (arbitrary JS
 * execution via a crafted font program in a malicious PDF). `disableAutoFetch`
 * keeps us from speculatively fetching the whole file. Both are forced on here
 * and cannot be overridden by the caller's extra options.
 *
 * @param {string} url   PDF url
 * @param {object} extra optional pdfjs getDocument params (e.g. an Abort...
 *                       not supported in 3.x; reserved for caller-side use).
 */
export async function getPdfDocument(url, extra = {}) {
  const pdfjs = await loadPdfjs();
  const task = pdfjs.getDocument({
    url,
    ...extra,
    // Forced security params — keep AFTER the spread so they can't be undone.
    isEvalSupported: false,
    disableAutoFetch: true,
  });
  return task.promise;
}

/**
 * Render a given page of a loaded PDF document onto a canvas at the given scale.
 * Returns the canvas. Caller owns mounting/sizing the canvas element.
 */
export async function renderPageToCanvas(pdf, pageNumber, canvas, scale = 1.0) {
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale });
  const context = canvas.getContext("2d");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  await page.render({ canvasContext: context, viewport }).promise;
  return canvas;
}
